import React, { FC, HTMLAttributes } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "src/components/ui/button";
import useStoreId from "src/hooks/useStoreId/useStoreId";

interface PreviousObservationProps extends HTMLAttributes<HTMLDivElement> {
	isFinished?: boolean;
	store: number | null;
	outcome?: string;
	formId: number;
	observationId: number;
	completedAt: string;
	updatedAt: string;
	observedAt?: string;
	userId?: number;
}

const PreviousObservation: FC<PreviousObservationProps> = ({
	outcome,
	updatedAt,
	completedAt,
	store: storeId,
	isFinished,
	formId,
	observationId,
	observedAt,
	userId,
	...restProps
}) => {
	const { data: store } = useStoreId(storeId, { enabled: Boolean(storeId) });

	const recordedDate = isFinished ? completedAt : updatedAt;

	return (
		<div
			className="border-y border-border/25 py-4"
			data-testid="previous-observation"
			{...restProps}
		>
			<div className="flex justify-between items-center">
				<div className="flex flex-col items-start gap-1">
					<div className="flex items-center gap-1">
						{isFinished ? (
							<>
								<span className="font-bold text-xs">Added on:</span>
								<span className="text-xs">{recordedDate}</span>
							</>
						) : (
							<>
								<span className="font-bold text-xs">Last Observation:</span>
								<span className="text-xs">{recordedDate}</span>
							</>
						)}
					</div>
					<div className="flex items-center gap-1">
						{isFinished && observedAt && (
							<>
								<span className="font-bold text-xs">Observed on:</span>
								<span className="text-xs">{observedAt}</span>
							</>
						)}
					</div>
					{store && (
						<div className="flex items-center gap-1">
							<span className="font-bold text-xs">Store:</span>
							<span className="text-xs">{store.name}</span>
						</div>
					)}
					<div className="flex items-center gap-1">
						<span className="font-bold text-xs">Outcome:</span>
						<span className="text-xs">{outcome}</span>
					</div>
				</div>
				{outcome === "Not Completed (In Progress)" && (
					<Button variant="outline" size="sm" asChild>
						<Link
							to={
								userId
									? `/observations/submissions/${formId}/user/${userId}/${observationId}`
									: `/observations/submissions/${formId}/store/${storeId}/${observationId}`
							}
						>
							Resume
							<RefreshCw className="h-3.5 w-3.5" />
						</Link>
					</Button>
				)}
			</div>
			{outcome === "Not Completed (In Progress)" && (
				<div className="flex items-center gap-1 opacity-60 mt-4">
					<AlertTriangle />
					<span className="text-xs">
						You will have 24 hours to resume this observation.
					</span>
				</div>
			)}
		</div>
	);
};

export default PreviousObservation;
